import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, authHeaders } from "@/lib/api";
import {
  Upload,
  Trash2,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB

function itemTransform(item) {
  return [
    `rotate(${item.rotation || 0}deg)`,
    `scaleX(${(item.flip_h ? -1 : 1) * (item.scale || 1)})`,
    `scaleY(${(item.flip_v ? -1 : 1) * (item.scale || 1)})`,
  ].join(" ");
}

function itemObjectPos(item) {
  const x = item.position_x == null ? 50 : item.position_x;
  const y = item.position_y == null ? 50 : item.position_y;
  return `${x}% ${y}%`;
}

export default function MarqueeManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get("/marquee");
      setItems(res.data || []);
    } catch {
      toast.error("Failed to load marquee images.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image must be under 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => {
      toast.error("Couldn't read that file. Try a different one.");
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    };
    reader.onload = async () => {
      setUploading(true);
      try {
        const res = await api.post(
          "/marquee",
          { data_url: reader.result },
          { headers: authHeaders(), timeout: 60000 }
        );
        setItems((prev) => [...prev, res.data]);
        toast.success("Image added.");
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const msg =
          status === 413
            ? "Image is too large after encoding. Try a smaller one."
            : status === 401
            ? "Session expired. Sign in again."
            : detail || err?.message || "Upload failed.";
        toast.error(typeof msg === "string" ? msg : "Upload failed.");
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const patchItem = async (id, changes, { silent = false } = {}) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...changes } : i)));
    try {
      const res = await api.patch(`/marquee/${id}`, changes, { headers: authHeaders() });
      setItems((prev) => prev.map((i) => (i.id === id ? res.data : i)));
    } catch {
      if (!silent) toast.error("Update failed.");
      fetchAll();
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/marquee/${id}`, { headers: authHeaders() });
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[idx];
    const b = items[j];
    // Optimistic reorder
    const next = [...items];
    next[idx] = b;
    next[j] = a;
    setItems(next.map((it, i) => ({ ...it, order: i })));
    try {
      await Promise.all([
        api.patch(`/marquee/${a.id}`, { order: j }, { headers: authHeaders() }),
        api.patch(`/marquee/${b.id}`, { order: idx }, { headers: authHeaders() }),
      ]);
    } catch {
      toast.error("Reorder failed.");
      fetchAll();
    }
  };

  return (
    <section
      className="border p-6 sm:p-8 mb-12"
      style={{
        background: "rgba(48,23,40,0.55)",
        borderColor: "rgba(226,211,226,0.22)",
        borderRadius: "8px",
      }}
      data-testid="marquee-manager"
    >
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="small-caps mb-2" style={{ color: "#B9929D" }}>
            Landing marquee
          </div>
          <h2 className="font-display" style={{ fontSize: "32px", color: "#F2EEE7", lineHeight: 1.1 }}>
            The <em style={{ color: "#DDBFCF" }}>scrolling</em> photo strip
          </h2>
        </div>
        <span className="text-xs italic" style={{ color: "#DDBFCF", opacity: 0.7 }}>
          If empty, the landing shows a default set of 5 seed images.
        </span>
      </div>

      {loading ? (
        <p className="italic" style={{ color: "#DDBFCF", opacity: 0.7 }}>Loading…</p>
      ) : items.length === 0 ? (
        <p className="italic mb-6" style={{ color: "#DDBFCF", opacity: 0.7 }}>
          No custom images yet — the landing is showing the default seed set. Add your first below.
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-6 mb-8" data-testid="marquee-items">
          {items.map((item, idx) => (
            <li
              key={item.id}
              className="flex gap-5 items-start p-4"
              style={{
                background: "rgba(24,10,19,0.5)",
                border: "1px solid rgba(226,211,226,0.15)",
                borderRadius: "8px",
              }}
              data-testid={`marquee-row-${item.id}`}
            >
              {/* Live preview matching marquee frame aspect (7:5) */}
              <div
                style={{
                  flex: "0 0 auto",
                  width: "196px",
                  height: "140px",
                  borderRadius: "6px",
                  overflow: "hidden",
                  background: "rgba(48,23,40,0.5)",
                }}
              >
                <img
                  src={item.data_url}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: itemObjectPos(item),
                    display: "block",
                    transform: itemTransform(item),
                    transformOrigin: "center center",
                    transition: "transform 250ms ease",
                  }}
                />
              </div>

              {/* Controls */}
              <div className="flex-1 min-w-0 space-y-4">
                {/* Order + actions row */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="small-caps mr-1" style={{ color: "#B9929D" }}>
                    #{idx + 1}
                  </span>
                  <IconButton
                    label="Move up"
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    testid={`move-up-${item.id}`}
                  >
                    <ArrowUp size={13} />
                  </IconButton>
                  <IconButton
                    label="Move down"
                    disabled={idx === items.length - 1}
                    onClick={() => move(idx, 1)}
                    testid={`move-down-${item.id}`}
                  >
                    <ArrowDown size={13} />
                  </IconButton>
                  <span className="mx-1" style={{ opacity: 0.3, color: "#DDBFCF" }}>|</span>
                  <IconButton
                    label="Rotate 90°"
                    onClick={() => patchItem(item.id, { rotation: ((item.rotation || 0) + 90) % 360 })}
                    testid={`rotate-${item.id}`}
                  >
                    <RotateCw size={13} />
                  </IconButton>
                  <IconButton
                    label="Flip horizontal"
                    active={item.flip_h}
                    onClick={() => patchItem(item.id, { flip_h: !item.flip_h })}
                    testid={`flip-h-${item.id}`}
                  >
                    <FlipHorizontal size={13} />
                  </IconButton>
                  <IconButton
                    label="Flip vertical"
                    active={item.flip_v}
                    onClick={() => patchItem(item.id, { flip_v: !item.flip_v })}
                    testid={`flip-v-${item.id}`}
                  >
                    <FlipVertical size={13} />
                  </IconButton>
                  <span className="ml-auto" />
                  <IconButton
                    label="Delete"
                    onClick={() => removeItem(item.id)}
                    testid={`delete-${item.id}`}
                    danger
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </div>

                {/* Sliders: scale + crop position */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <SliderControl
                    label="Zoom"
                    value={item.scale || 1}
                    min={0.5}
                    max={2.5}
                    step={0.05}
                    formatValue={(v) => `${v.toFixed(2)}×`}
                    onChange={(v) => patchItem(item.id, { scale: v }, { silent: true })}
                    testid={`scale-${item.id}`}
                  />
                  <SliderControl
                    label="Crop X"
                    value={item.position_x == null ? 50 : item.position_x}
                    min={0}
                    max={100}
                    step={1}
                    formatValue={(v) => `${Math.round(v)}%`}
                    onChange={(v) => patchItem(item.id, { position_x: v }, { silent: true })}
                    testid={`posx-${item.id}`}
                  />
                  <SliderControl
                    label="Crop Y"
                    value={item.position_y == null ? 50 : item.position_y}
                    min={0}
                    max={100}
                    step={1}
                    formatValue={(v) => `${Math.round(v)}%`}
                    onChange={(v) => patchItem(item.id, { position_y: v }, { silent: true })}
                    testid={`posy-${item.id}`}
                  />
                </div>

                <div className="text-[10px] italic" style={{ color: "#DDBFCF", opacity: 0.55 }}>
                  Rotation: {item.rotation || 0}° · Flip H: {item.flip_h ? "on" : "off"} · Flip V:{" "}
                  {item.flip_v ? "on" : "off"}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid="marquee-file-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="marquee-upload-btn"
          className="btn-primary"
        >
          <Upload size={14} /> {uploading ? "Uploading…" : "Add image"}
        </button>
      </div>
    </section>
  );
}

function SliderControl({ label, value, min, max, step, formatValue, onChange, testid }) {
  return (
    <div>
      <div className="small-caps mb-1 flex justify-between" style={{ color: "#B9929D" }}>
        <span>{label}</span>
        <span style={{ color: "#F2EEE7" }}>{formatValue(Number(value))}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        className="w-full"
        style={{ accentColor: "#b8c975" }}
      />
    </div>
  );
}

function IconButton({ children, onClick, active, danger, disabled, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testid}
      className="p-2 transition-colors"
      style={{
        background: active ? "rgba(184,201,117,0.2)" : "transparent",
        border: "1px solid rgba(226,211,226,0.3)",
        borderRadius: "6px",
        color: danger ? "#f0b0a8" : active ? "#b8c975" : "#F2EEE7",
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}
